import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Finance } from './pages/Finance';
import { ClientsSuppliers } from './pages/ClientsSuppliers';
import { HRM } from './pages/HRM';
import { Inventory } from './pages/Inventory';
import { Invoicing } from './pages/Invoicing';
import { Outsourcing } from './pages/Outsourcing';
import { OutsourcingWorker } from './pages/OutsourcingWorker';
import { Settings } from './pages/Settings';
import { Reports } from './pages/Reports';
import { Sidebar } from './components/Sidebar';
import { ClientDetail } from './components/finance/ClientDetail';
import { SupplierDetail } from './components/finance/SupplierDetail';
import { canAccessPage, getDefaultRedirect } from './utils/permissions';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!canAccessPage(user.role, location.pathname)) {
    return <Navigate to={getDefaultRedirect(user.role)} replace />;
  }

  return (
    <div className="flex layout-container">
      <Sidebar />
      <div className="flex-1 overflow-auto">{children}</div>
    </div>
  );
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={getDefaultRedirect(user.role)} replace /> : <Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <Finance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients-suppliers"
        element={
          <ProtectedRoute>
            <ClientsSuppliers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients-suppliers/client/:id"
        element={
          <ProtectedRoute>
            <ClientDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients-suppliers/supplier/:id"
        element={
          <ProtectedRoute>
            <SupplierDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hrm/*"
        element={
          <ProtectedRoute>
            <HRM />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/*"
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoicing/*"
        element={
          <ProtectedRoute>
            <Invoicing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outsourcing"
        element={
          <ProtectedRoute>
            <Outsourcing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outsourcing/worker/:id"
        element={
          <ProtectedRoute>
            <OutsourcingWorker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;